var __decorate = this && this.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
    r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
    d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};
/* eslint-disable no-underscore-dangle */
import Vue from 'vue';
import Component from 'vue-class-component';
import { SyncWaterfallHook, SyncHook, AsyncSeriesHook, SyncBailHook } from 'tapable';
import { mapValues, values, forEach, groupBy, flatten, cloneDeep, uniqBy, keyBy, isEqual, isObject, get } from "lodash-es";
import { defaultConfig, defaultConfigDeepMergeKeys } from '../defaults/config';
import { toKebabCase } from "../../lib/utils/string";
import { CompletedComponentMeta } from './components';
import { AUTO_COMPONENT_ID_PREFIX, DEFAULT_EDITORS_FIT_DATA_ORDER, FIT_DATA_TYPE_META } from "../../lib/utils/consts";
import { builtinPlugins } from "../../lib/builtin/plugins";
import { getCompletedAtom } from '../simpleFlow/getCompletedAtom';
import { getBusUIMethods } from './ui';
import { upgradePagelets } from "../../lib/utils/layout/upgradePagelets";
import { getFitDataLayout, walkLayout } from "../../lib/utils";
import { arrayify } from "../../lib/utils/array";
import './vuePatch';
import defaultComponentIcon from "../../lib/assets/defaultComponentIcon.png";
const createUcBusHooks = () => {
  const hooks = {
    /**
     * 当用户想获取一个指定数据源的备选组件时，UICore 会逐个的调用组件的 fitData 方法，并生成一个预设列表。
     *
     * 这个钩子会在预设列表生成后，实际展示给用户前调用。
     * 此时可以对 fitDataResults（第一个参数）的内容做调整，改变预设列表。
     *
     * 需要注意，钩子被调用时，预设列表的顺序可能为乱的。UICore 在钩子返回后，才执行排序逻辑
     */
    fitDataAfterAll: new SyncWaterfallHook(['fitDataResults', 'fitDataInfo']),
    /**
     * 当用户将一个fitData出来的组件拖拽放到页面上之后，触发此钩子
     *
     * 如果 fitData 本身有自定义 `onApply` 逻辑，这个会在其之后执行
     */
    fitDataOnApply: new SyncWaterfallHook(['fitDataResultOnApplyCtx', 'fitDataResult']),
    /**
     * 这个钩子会在 uc-renderer / uc-designer 的 beforeCreate 阶段被调用
     *
     * 按照 [Vue 的设计和测试用例](https://github.com/vuejs/vue/blob/2.6/test/unit/features/options/lifecycle.spec.js)，
     * 此时你可对 `rendererInstance.$options` 做修改从而注入一些自己的逻辑。
     *
     * 需要留意，在 beforeCreate 时很多东西都是空的，但是你可以在这里添加 hook:created 事件或者对应生命周期，实现其他逻辑
     */
    rootBeforeCreate: new SyncHook(['root']),
    /**
     * 这个钩子会在 CRenderer 的 beforeCreate 阶段被调用
     *
     * 按照 [Vue 的设计和测试用例](https://github.com/vuejs/vue/blob/2.6/test/unit/features/options/lifecycle.spec.js)，
     * 此时你可对 `rendererInstance.$options` 做修改从而注入一些自己的逻辑。
     *
     * 需要留意，在 beforeCreate 时 rendererInstance 的 data 是空的。
     * 如果你需要访问，请考虑在 `pageletRendererCreated` 这个钩子里扩展功能。
     */
    pageletRendererBeforeCreate: new SyncHook(['rendererInstance', 'pagelet']),
    /**
     * 这个钩子会在 CRenderer 的 created 阶段被调用，此时 data、computed 等等已经初始化完了
     */
    pageletRendererCreated: new SyncHook(['rendererInstance', 'pagelet']),
    /**
     * 每一个页面片实例都会有一个根 DataStub，其中包含的字段是由“页面片数据源”（见文档）决定的，
     *
     * 这个钩子会在 CRenderer 整合了全部数据源，合成了一个巨大的 DataSchema，但是还没初始化 DataStub 的时候被调用
     *
     * 此时你可以修改 dataSchema，比如添加或者删除一些字段。（注意，这里只是修改数据结构信息，不是真的加了字段）
     */
    pageletDataSchemaAfterCompose: new SyncWaterfallHook(['dataSchema', 'rendererInstance']),
    /**
     * 这个钩子会在 UICore 准备 components 的时候被调用
     *
     * 可以对 componentMeta[] 做修改并返回修改后的新 componentMeta[]（如果不修改，请返回 undefined）
     */
    componentMetaListAfterLoad: new SyncWaterfallHook(['componentMetaList']),
    /**
     * 这个钩子会在 UICore 准备 flowAtoms 的时候被调用
     *
     * 可以对 SimpleFlowAtom[] 做修改并返回修改后的新 SimpleFlowAtom 列表（如果不修改，请返回 undefined）
     */
    flowAtomListAfterLoad: new SyncWaterfallHook(['atomList']),
    /**
     * 这个钩子会在编辑模式，UICore 为某个组件准备 actions 操作按钮时调用。
     *
     * 执行时机为组件的 getActions 执行后，实际渲染前。如果组件没有 getActions，不影响这个钩子的执行的。
     *
     * 可以对 result 做修改并返回修改后的新 actions 列表（如果不修改，请返回 undefined）
     */
    getActionsOfComponent: new SyncWaterfallHook(['result', 'ctx']),
    /**
     * 这个钩子会在编辑模式，左侧界面，UICore 为每一个数据源字段补充操作按钮时使用。
     *
     * 可以对 result 做修改并返回修改后的新 actions 列表（如果不修改，请返回 undefined）
     */
    getActionsOfDataSource: new SyncWaterfallHook(['result', 'ctx']),
    /**
     * 这个钩子会在编辑模式，用户为组件 添加 / 取消绑定 数据源的时候触发
     *
     * 如果 bindingMeta 里有定义 onBind，那么会在他 **之后** 执行
     */
    setBinding: new AsyncSeriesHook(['req']),
    /**
     * 这个钩子会在编辑模式，启用mock数据源时触发
     *
     * 通过参数传入新的mockCase到MockProxyPlugin中
     */
    setMockState: new SyncHook(['isMockOn', 'mockCase', 'designer']),
    /**
     * 在CRenderer的created中，对数据源变化进行监听之前进行调用，是最先操作ucDataSources的位置
     */
    pageletRendererCreatedMockStart: new SyncHook(['rendererInstance']),
    /**
     * 在CRenderer的collectCRendererRuntimeData中被使用
     * 收集CRenderer的ucDataSources作为mock数据
     */
    pageletRendererCollectRuntimeData: new SyncWaterfallHook(['mockData', 'rendererInstance']),
    /**
     * 返回的数组有内容时 会在组件的属性面板里显示组件替换按钮 按照ReplaceComponentOption中的字段进行分类排序
     */
    getReplaceComponent: new SyncWaterfallHook(['result', 'ctx']),
    /**
     * 在调用 uc.openDevtool 前和后触发
     * 在方法里可以插入一些自定义面板
     */
    onOpenDevTool: new SyncWaterfallHook(['devTool']),
    onOpenDevToolDone: new SyncWaterfallHook(['devTool']),
    /**
     * 在styleLoader和VNodeFactory判别是否有动态样式时使用
     *
     * 如果你需要做单位转换，则必须将样式标记为【动态】的，否则 `convertStyle` 钩子不会执行。
     *
     * 如果需要标记为动态的，返回 `true` 即可。
     *
     * @example
     *
     * ```js
     * (style, ctx) => {
     *   return some(style, (value, key) => {
     *     return value.includes('px')
     *   })
     * }
     * ```
     */
    beforeConvertStyle: new SyncBailHook(['style', 'ctx']),
    /**
     * 在动态样式运算时调用 可以批量的进行样式修改 如转换 rpx 为 px
     *
     * 需要在 `beforeConvertStyle` 先将样式标记为【动态】的，否则这个钩子不会执行
     *
     * @example
     *
     * ```js
     * // 只有通过了beforeConvertFn判别的样式 才会到这里 可以修改样式 返回新的style
     * (style, ctx) => {
     *   style = mapValues(style,(value,key)=>{
     *     if (key==='width') return "200px";   // 例子: 如果遇到 width 就使用固定值
     *     return value;
     *   })
     *   style['height'] = "100px"; // 例子：强制写入样式 height
     *   return style;
     * }
     * ```
     */
    convertStyle: new SyncWaterfallHook(['style', 'ctx'])
  };
  // SyncBailHook 改造为 只有返回true的时候退出
  hooks.beforeConvertStyle.intercept({
    register: options => {
      const originTapFun = options.fn;
      options.fn = (...args) => !!originTapFun(...args) || undefined;
      return options;
    }
  });
  return hooks;
};
// export type UcBus = ReturnType<typeof createBus>
let UcBus = class UcBus extends Vue.extend({
  name: 'UcBus',
  props: {
    root: {
      type: null,
      required: true
    },
    builtinAssets: {
      type: Object,
      default: () => ({})
    },
    configOverrides: {
      type: Object,
      default: () => ({})
    }
    // isDesignMode 直接在 beforeCreate 里从 propData 读取，免得多一大堆的奇怪依赖
  },
  methods: {},
  created() {
    Object.assign(this, getBusUIMethods(this));
  }
}) {
  constructor() {
    super(...arguments);
    this.config = {
      ...defaultConfig
    };
    this.designer = null;
    this.renderer = null;
    this.version = "1.149.0";
    this.pagelets = []; // 注意这里保存的是当前渲染用的未提交的版本
    this.pageletsChanged = false;
    this.componentPlugins = {}; // 全局注册的组件插件
    this.rendererSet = new Set(); // 收集全局的CRenderer实例
  }
  beforeCreate() {
    var _this$$options$propsD;
    this.isDesignMode = !!((_this$$options$propsD = this.$options.propsData) !== null && _this$$options$propsD !== void 0 && _this$$options$propsD.isDesignMode);
  }
  created() {
    var _this$root$$options$p;
    // 准备 hooks
    this.hooks = createUcBusHooks();
    // 准备 config
    Object.keys(defaultConfig).forEach(key => {
      const kebabKey = toKebabCase(key);
      const getConfigItemFromRoot = () => {
        var _this$configOverrides, _this$root$$attrs$key, _this$root$$attrs, _this$root$$attrs2;
        const defaultValue = (_this$configOverrides = this.configOverrides[key]) !== null && _this$configOverrides !== void 0 ? _this$configOverrides : defaultConfig[key];
        const val = (_this$root$$attrs$key = (_this$root$$attrs = this.root.$attrs) === null || _this$root$$attrs === void 0 ? void 0 : _this$root$$attrs[key]) !== null && _this$root$$attrs$key !== void 0 ? _this$root$$attrs$key : (_this$root$$attrs2 = this.root.$attrs) === null || _this$root$$attrs2 === void 0 ? void 0 : _this$root$$attrs2[kebabKey];
        if (typeof val === 'undefined') return defaultValue;
        if (val === '' && typeof defaultValue === 'boolean') return true;
        if (defaultConfigDeepMergeKeys.includes(key)) return {
          ...defaultValue,
          ...val
        };
        return val;
      };
      this.$watch(getConfigItemFromRoot, val => {
        this.config[key] = val;
      }, {
        immediate: true
      });
    });
    const installPlugin = (plugin, pluginName) => {
      try {
        var _plugin$install;
        plugin === null || plugin === void 0 || (_plugin$install = plugin.install) === null || _plugin$install === void 0 || _plugin$install.call(plugin, this);
      } catch (e) {
        console.error(`[UICore] ${pluginName}插件加载失败`, e);
      }
    };
    // 加载插件
    forEach(builtinPlugins, installPlugin);
    (_this$root$$options$p = this.root.$options.propsData.plugins) === null || _this$root$$options$p === void 0 || _this$root$$options$p.forEach(installPlugin);
    this.root.$once('hook:created', () => {
      // 如果外部传给 uc-designer / uc-renderer 的 pagelets 变了，就同步下
      this.$watch(() => upgradePagelets(this, this.root.$props.pagelets) || this.root.$props.pagelets, newPagelets => {
        if (!isEqual(this.pagelets, newPagelets)) {
          const staticRenderer = !this.isDesignMode && this.config.ucStaticRenderer;
          // 如果是干净的对象 并且在运行时优化下可以考虑不克隆
          const notNeedClone = staticRenderer && newPagelets.every(pagelet => !pagelet.__ob__);
          const pagelets = notNeedClone ? newPagelets : cloneDeep(newPagelets);
          if (staticRenderer) {
            pagelets.forEach(item => {
              Object.defineProperty(item, '_isVue', {
                value: true,
                enumerable: false
              });
            });
          }
          this.pagelets = pagelets;
        }
      }, {
        immediate: true,
        deep: true
      });
    });
    this.root.$once('hook:created', () => {
      var _this$root$$props$pag;
      // @ts-ignore
      this.ucFastMode = !!((_this$root$$props$pag = this.root.$props.pagelets) !== null && _this$root$$props$pag !== void 0 && (_this$root$$props$pag = _this$root$$props$pag[0]) !== null && _this$root$$props$pag !== void 0 && _this$root$$props$pag.ucFastMode || this.config.ucFastMode || localStorage.getItem('uicore:fastMode'));
    });
    // this.notifyPageletsChange = vueDebounce(() => {
    //   this.root.$emit('update:pagelets', this.temp1);
    // });
  }
  beforeDestroy() {
    this.flushPageletsChange();
    clearTimeout(this[pageletsChangeAutoFlushTimer]);
  }
  /**
   * （设计模式用的）
   */
  upsertPagelet(pagelet) {
    const pagelets = [...this.pagelets];
    const idx = pagelets.findIndex(x => x.id === pagelet.id);
    if (idx === -1) pagelets.push(pagelet);else pagelets.splice(idx, 1, {
      ...pagelets[idx],
      ...pagelet
    });
    this.pagelets = pagelets;
    this.notifyPageletsChange();
  }
  /**
   * 传入一个组件，获取他的名字
   */
  getLayoutName(layout, opts = {}) {
    var _this$components$type;
    if (!layout) return '';
    const {
      type,
      componentId
    } = layout;
    const hasComponentId = !opts.omitId && componentId && !componentId.startsWith(AUTO_COMPONENT_ID_PREFIX);
    const compMeta = (_this$components$type = this.components[type]) === null || _this$components$type === void 0 ? void 0 : _this$components$type.meta;
    let typeName = !compMeta ? `未知组件${type}` : `${compMeta.name || type}`;
    if (typeof (compMeta === null || compMeta === void 0 ? void 0 : compMeta.getLayoutName) === 'function') typeName = compMeta.getLayoutName(layout) || typeName;
    if (hasComponentId) return `${componentId} (${typeName})`;
    return typeName;
  }
  /**
   * 给定一个 dataPath，根据当前页面片，看看有那些推荐的组件
   *
   * 最后结果是排序了的
   */
  fitData(dataPathOrDataStub, renderer = (_this$designer => (_this$designer = this.designer) === null || _this$designer === void 0 ? void 0 : _this$designer.renderer)() || this.renderer, options = {}) {
    var _dataStub$schema$ui;
    if (!renderer) return [];
    const dataStub = typeof dataPathOrDataStub === 'string' ? renderer.getStubAtPath(dataPathOrDataStub) : dataPathOrDataStub;
    if (!dataStub) return [];
    if (dataStub.schema['uc:bindable'] === false) return [];
    const fitDataInfo = {
      uc: this,
      renderer,
      dataPath: dataStub.path,
      dataStub,
      dataSchema: dataStub.schema,
      options
    };
    let ans = [];
    // 遍历全部组件
    forEach(this.components, (meta, compName) => {
      if (this.fitDataSkipComp(meta, compName, options)) return;
      let rawAns;
      try {
        rawAns = meta.fitData(fitDataInfo);
      } catch (err) {
        console.error(`[UICore] Component ${meta.id} failed to fitData. `, err, fitDataInfo);
      }
      if (!rawAns) return;
      if (!Array.isArray(rawAns)) rawAns = [rawAns];else rawAns = rawAns.filter(Boolean);
      if (!rawAns.length) return;
      rawAns.forEach(state => {
        // 组件定义 layout 时可以偷懒不写 type
        const {
          layout,
          layouts
        } = getFitDataLayout(state);
        if (Object.keys(layout).length && !layout.type) {
          state.layout = {
            ...layout,
            type: meta.id
          };
        }
        if (layouts.length) {
          layouts.forEach(innerState => {
            if (!innerState.layout.type) {
              Object.assign(innerState.layout, {
                type: meta.id
              });
            }
          });
        }
      });
      const base = {
        desc: meta.desc,
        icon: meta.icon,
        name: meta.name || meta.id,
        order: 100,
        subTitle: '',
        onApply: () => void 0,
        inheritAttrs: true
      };
      const completedAns = rawAns.map(x => ({
        ...base,
        ...x
      }));
      ans.push(...completedAns);
    });
    // 关于 DataSchema 的特殊逻辑：如果配置了 ui.type 那么要强制推荐对应的组件
    // 此时 ui.props、style 和 className 也要生效
    const predefinedUiType = (_dataStub$schema$ui = dataStub.schema.ui) === null || _dataStub$schema$ui === void 0 ? void 0 : _dataStub$schema$ui.type;
    const predefineUiComponent = predefinedUiType && this.components[predefinedUiType];
    if (predefinedUiType && predefineUiComponent && predefineUiComponent.mainBinding) {
      let bestEditorOrder = DEFAULT_EDITORS_FIT_DATA_ORDER;
      const ui = dataStub.schema.ui;
      ans.forEach(item => {
        if (item.type !== 'editor') return;
        if (typeof item.order === 'number' && bestEditorOrder > item.order) bestEditorOrder = item.order;
      });
      const layout = {
        type: predefinedUiType,
        props: cloneDeep({
          ...ui.props
        }),
        bindings: {
          [predefineUiComponent.mainBindingId]: dataStub.path
        }
      };
      if (isObject(ui.style)) layout.style = cloneDeep(ui.style);
      if (ui.className) layout.class = ui.className;
      ans.unshift({
        type: 'editor',
        desc: '在 DataSchema 中指定的',
        icon: predefineUiComponent.icon || defaultComponentIcon,
        name: `${predefineUiComponent.name || predefinedUiType}`,
        order: bestEditorOrder,
        layout,
        subTitle: '',
        onApply: () => void 0,
        inheritAttrs: true
      });
    }
    // 插件后处理
    ans = this.hooks.fitDataAfterAll.call(ans, fitDataInfo);
    // 排序
    // 先按照组内排序，再按照组排序（比如 editor 最优先）
    return this.fitDataSort(ans);
  }
  /** 给fitdata的结果按双字段排序 */
  fitDataSort(ans) {
    const groupedSorted = mapValues(groupBy(ans, v => v.type), val => val.sort((a, b) => a.order - b.order));
    const temp = [];
    forEach(FIT_DATA_TYPE_META, (v, k) => {
      temp[v.order] = groupedSorted[k] || [];
      delete groupedSorted[k];
    });
    return flatten(temp.concat(values(groupedSorted)));
  }
  /** fitData是否跳过当前的组件
   * 默认compTypeFilter为'core'筛选
   * 左侧面板组件推荐与更换组件使用'all'筛选 */
  fitDataSkipComp(meta, compName, options) {
    const compTypeFilter = options.compTypeFilter || 'core';
    const filterSkipIsNotCore = compTypeFilter === 'core' && meta.isCoreComponent === false;
    const filterSkipCustomArray = Array.isArray(compTypeFilter) && !compTypeFilter.includes(compName);
    return filterSkipIsNotCore || filterSkipCustomArray;
  }
  get ui() {
    return this.config.uiLib;
  }
  /**
   * 已经注册的组件的 meta
   *
   * 注意。各种组件，包括 type 不是 normal 的组件，都会出现在这
   */
  get componentsArray() {
    const rawList = uniqBy([...(this.root.components || []), ...(this.builtinAssets.components || [])].filter(Boolean), 'id');
    const metaList = this.hooks.componentMetaListAfterLoad.call(rawList);
    return metaList.map(component => new CompletedComponentMeta(component));
  }
  /**
   * 已经注册的组件，的各种具体信息和工具方法
   *
   * 注意。各种组件，包括 type 不是 normal 的组件，都会出现在这
   */
  get components() {
    return keyBy(this.componentsArray, 'id');
  }
  /**
   * 已经注册的事件流程 atom
   */
  get flowAtomsArray() {
    const rawList = uniqBy([...(this.root.flowAtoms || []), ...(this.builtinAssets.flowAtoms || [])].filter(Boolean), 'id');
    return this.hooks.flowAtomListAfterLoad.call(rawList).map(getCompletedAtom);
  }
  /**
   * 已经注册的组件，的各种具体信息和工具方法
   */
  get flowAtoms() {
    return keyBy(this.flowAtomsArray, 'id');
  }
  get designerComponentsArray() {
    return uniqBy([...this.root.designerComponents, ...(this.builtinAssets.designerComponents || []), ...this.componentsArray].filter(Boolean), 'id');
  }
  get designerComponents() {
    return keyBy(this.designerComponentsArray, 'id');
  }
  get pageletLUT() {
    return keyBy(this.pagelets, 'id');
  }
  getDataFormContextOptions({
    extraComponents = [],
    immutable = false
  }) {
    // UICore 这些组件和 DataForm 不完全兼容
    const blacklist = {
      textInput: true,
      basicForm: true,
      select: true,
      numberInput: true,
      arrayTable: true,
      checkbox: true
    };
    const components = uniqBy([...(extraComponents || []), ...this.designerComponentsArray.filter(x => !(x.id in blacklist))], 'id');
    return {
      ...this.config.propsDSOptions,
      requiredByDefault: true,
      immutable,
      setter: this.$set,
      components
    };
  }
  notifyPageletsChange(delay = this.config.designerCommitDebounceDuration) {
    this.pageletsChanged = true;
    // 如果这次delay=0 然后同步的立刻delay=3000呢?  0的那次会被忽略掉 导致undo延迟
    // 所以应该是尽可能的早触发notify
    const now = new Date().getTime();
    let flushDelay = delay;
    // 如果之前有定时器了 要取最近能触发flushPageletsChange的时间点
    if (this[pageletsChangeAutoFlushTimer]) {
      clearTimeout(this[pageletsChangeAutoFlushTimer]);
      const lastFlushDelay = this[pageletsChangeFlushTime] - now;
      flushDelay = Math.min(lastFlushDelay, delay);
    }
    this[pageletsChangeAutoFlushTimer] = setTimeout(() => this.flushPageletsChange(), flushDelay);
    this[pageletsChangeFlushTime] = flushDelay + now;
  }
  flushPageletsChange(force = false) {
    if (get(this, '_isDestroyed')) return;
    if (!force && !this.pageletsChanged) return;
    if (this[pageletsChangeAutoFlushTimer]) clearTimeout(this[pageletsChangeAutoFlushTimer]);
    this.pageletsChanged = false;
    this.root.$emit('update:pagelets', cloneDeep(this.pagelets));
  }
  /**
   * 递归地，根据组件 initialLayout 和插槽默认值，补全 Layout
   *
   * 注意，这里会直接改写 layout 这个入参数！！
   *
   * 另见 CompletedComponentMeta 的 completeLayout
   */
  completeLayout(layout) {
    arrayify(layout).forEach(root => {
      walkLayout(root, ({
        layout
      }) => {
        var _this$components$layo;
        (_this$components$layo = this.components[layout.type]) === null || _this$components$layo === void 0 || _this$components$layo.completeLayout(layout);
      }, {
        includeScopedSlots: true
      });
    });
  }
  /** 传入组件Id 返回所有渲染层级中 符合该Id的Vue实例数组 */
  getVueInstanceByCompId(compId) {
    return Array.from(this.rendererSet.values()).map(renderer => {
      const compArray = arrayify(renderer.$refs[compId]);
      return compArray;
    }).flat();
  }
};
UcBus = __decorate([Component], UcBus);
export { UcBus };
const pageletsChangeAutoFlushTimer = Symbol('pageletsChangeAutoFlushTimer');
const pageletsChangeFlushTime = Symbol('pageletsChangeFlushTime');
/**
 * @param root publicComp 实例
 */
export function createBus(root, {
  isDesignMode = false,
  builtinAssets = {},
  ...configOverrides
} = {}) {
  return new UcBus({
    propsData: {
      root,
      isDesignMode,
      builtinAssets,
      configOverrides
    },
    parent: root
  });
}